import React, { useEffect, useState } from 'react';
import {
  getCategories, createCategory, updateCategory, deleteCategory,
  getInventoryParts, createInventoryPart, updateInventoryPart, deleteInventoryPart,
} from '../../services/api';
import { PageHeader } from '../../components/Shared';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Plus, Pencil, Trash2, Tag, Clock } from 'lucide-react';
import { toast } from 'sonner';

const InventorySettings = () => {
  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="inventory-settings-page">
      <PageHeader
        title="Inventory Settings"
        description="Categorise your parts (with markup defaults) and manage hourly labour rates used on Work Orders."
      />
      <Tabs defaultValue="categories">
        <TabsList>
          <TabsTrigger value="categories" data-testid="settings-tab-categories">
            <Tag className="h-4 w-4 mr-1.5" /> Categories
          </TabsTrigger>
          <TabsTrigger value="rates" data-testid="settings-tab-rates">
            <Clock className="h-4 w-4 mr-1.5" /> Labour rates
          </TabsTrigger>
        </TabsList>
        <TabsContent value="categories" className="pt-4 space-y-4">
          <CategoriesPanel />
        </TabsContent>
        <TabsContent value="rates" className="pt-4 space-y-4">
          <LabourRatesPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
};

const CategoriesPanel = () => {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', markupPct: 0 });
  const [saving, setSaving] = useState(false);

  const refresh = () => getCategories().then(setCats).catch(() => {});
  useEffect(() => { refresh().finally(() => setLoading(false)); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ name: '', markupPct: 0 });
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, markupPct: c.markupPct });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await updateCategory(editing.id, form);
        toast.success('Category updated');
      } else {
        await createCategory(form);
        toast.success(`Category "${form.name}" created`);
      }
      setDialogOpen(false);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"?`)) return;
    try {
      await deleteCategory(c.id);
      toast.success('Deleted');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  if (loading) return <div className="text-slate-500">Loading…</div>;

  return (
    <>
      <div className="flex justify-end">
        <Button size="sm" onClick={openNew} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="add-category-btn">
          <Plus className="h-4 w-4 mr-2" /> New category
        </Button>
      </div>

      {cats.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-200 rounded-md p-12 text-center">
          <Tag className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <div className="font-display uppercase tracking-wide text-slate-700 mb-1">No categories yet</div>
          <div className="text-sm text-slate-500 mb-4">Create your first category to start applying default markups.</div>
          <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="add-first-category-btn">
            <Plus className="h-4 w-4 mr-2" /> Add first category
          </Button>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Category</th>
                <th className="text-right px-4 py-3">Markup %</th>
                <th className="text-right px-4 py-3 hidden sm:table-cell">Example: cost £10 → sell</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {cats.map(c => (
                <tr key={c.id} data-testid={`category-row-${c.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{c.name}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{Number(c.markupPct).toFixed(1)}%</td>
                  <td className="px-4 py-3 text-right text-slate-500 tabular-nums hidden sm:table-cell">
                    £{(10 * (1 + c.markupPct / 100)).toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`edit-cat-${c.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(c)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`delete-cat-${c.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="category-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit category' : 'New category'}</DialogTitle>
            <DialogDescription>
              {editing
                ? 'Updating the markup % only affects future draft inventory items, never existing ones.'
                : 'Categories like "Fasteners" or "MCBs" let draft inventory items inherit a sell-price = cost × (1 + markup).'
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input className="mt-1.5" value={form.name} autoFocus
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Fasteners"
                data-testid="category-name-input" />
            </div>
            <div>
              <Label>Markup %</Label>
              <Input className="mt-1.5" type="number" step="0.5" value={form.markupPct}
                onChange={e => setForm({ ...form, markupPct: parseFloat(e.target.value) || 0 })}
                placeholder="40"
                data-testid="category-markup-input" />
              <div className="text-xs text-slate-500 mt-1">
                Example: cost £10, markup 40% → sell £{(10 * (1 + (Number(form.markupPct) || 0) / 100)).toFixed(2)}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving}
              className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-category-btn">
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

/**
 * LabourRatesPanel — dedicated UI for hourly rates (non-tracked Xero
 * items used by the Work Order labour panel). Edits go straight to
 * `parts_v2`; the WO labour dropdown picks them up via the same
 * `xeroIsTracked === false` filter.
 */
const LabourRatesPanel = () => {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', sellPrice: 35 });
  const [saving, setSaving] = useState(false);

  const refresh = () => getInventoryParts()
    // Same filter as WorkOrderLaborPanel — keep a single definition of
    // "what counts as a labour rate" so the two views can never disagree.
    .then(all => setRates(all.filter(p =>
      !p.isDraft && (p.category === 'Labor' || p.xeroIsTracked === false)
    )))
    .catch(() => {});
  useEffect(() => { refresh().finally(() => setLoading(false)); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ name: '', sellPrice: 35 });
    setDialogOpen(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      name: r.name,
      sellPrice: r.sellPrice ?? r.unitCost ?? 0,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Name required');
    if (!form.sellPrice || Number(form.sellPrice) <= 0) return toast.error('Hourly rate must be > 0');
    setSaving(true);
    try {
      if (editing) {
        await updateInventoryPart(editing.id, {
          name: form.name.trim(),
          sellPrice: Number(form.sellPrice),
        });
        toast.success('Rate updated');
      } else {
        // Mark as labour by setting category='Labor' AND xeroIsTracked=false
        // — either signal alone is enough for the filter, but having both
        // means the rate shows up immediately even before any Xero sync.
        await createInventoryPart({
          name: form.name.trim(),
          sku: 'LAB-' + Date.now().toString(36).toUpperCase(),
          description: '',
          category: 'Labor',
          unitCost: 0,
          sellPrice: Number(form.sellPrice),
          minStock: 0,
          xeroIsTracked: false,
        });
        toast.success(`Rate "${form.name}" added`);
      }
      setDialogOpen(false);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete rate "${r.name}"?`)) return;
    try {
      await deleteInventoryPart(r.id);
      toast.success('Deleted');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  if (loading) return <div className="text-slate-500">Loading…</div>;

  return (
    <>
      <div className="flex justify-end">
        <Button size="sm" onClick={openNew} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="add-rate-btn">
          <Plus className="h-4 w-4 mr-2" /> New rate
        </Button>
      </div>

      {rates.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-200 rounded-md p-12 text-center">
          <Clock className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <div className="font-display uppercase tracking-wide text-slate-700 mb-1">No labour rates yet</div>
          <div className="text-sm text-slate-500 mb-4">Add one for each hourly band — Standard, Overtime, Emergency, etc. They show up instantly in the Work Order labour dropdown.</div>
          <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="add-first-rate-btn">
            <Plus className="h-4 w-4 mr-2" /> Add first rate
          </Button>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Rate name</th>
                <th className="text-right px-4 py-3">£ / hour</th>
                <th className="text-left px-4 py-3 hidden sm:table-cell">Xero</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rates.map(r => (
                <tr key={r.id} data-testid={`rate-row-${r.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{r.name}</td>
                  <td className="px-4 py-3 text-right tabular-nums">£{(r.sellPrice ?? r.unitCost ?? 0).toFixed(2)}</td>
                  <td className="px-4 py-3 text-xs text-slate-500 hidden sm:table-cell">
                    {r.xeroItemId
                      ? <span className="text-emerald-700">Synced ({r.xeroCode})</span>
                      : <span className="text-slate-400 italic">local only</span>
                    }
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(r)} data-testid={`edit-rate-${r.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(r)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`delete-rate-${r.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="rate-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit labour rate' : 'New labour rate'}</DialogTitle>
            <DialogDescription>
              {editing
                ? 'Changes apply to new work-order labour entries. Past entries keep their captured rate.'
                : 'A labour rate is an hourly band you bill for engineer time. Examples: "Standard £35/h", "Out-of-hours £55/h".'
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input className="mt-1.5" value={form.name} autoFocus
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Standard hour"
                data-testid="rate-name-input" />
            </div>
            <div>
              <Label>£ / hour</Label>
              <Input className="mt-1.5" type="number" step="0.5" min="0" value={form.sellPrice}
                onChange={e => setForm({ ...form, sellPrice: e.target.value })}
                data-testid="rate-price-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving}
              className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-rate-btn">
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InventorySettings;
